import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo } from 'react';

import { useHeaderOptions } from '../../model';
import { handleHeaderItemClick } from '../../utils';

import { useLivescore } from '@hooks/useLivescore';
import Image from '@shared/Image';
import { useTranslateFn } from '@shared/Translate';
import type { MenuItem } from '@shared-types/menu.types';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const compOnlyId = new Set(['comp_gaming', 'comp_uefatv']);

const QuickLinks = ({ item, isCompetition }: { item: MenuItem; isCompetition?: boolean }) => {
  const { options } = useHeaderOptions();
  const t = useTranslateFn();
  const liveMatches = useLivescore(options.liveScoreCompetitions);

  const competitionOnlyItems = item.menuItems.filter((m) => compOnlyId.has(m.id));
  const commonItems = item.menuItems.filter((m) => !compOnlyId.has(m.id));

  return (
    <div className={styles.spacer}>
      <div
        className={clsx(
          'pk-d-md--none pk-mb--l pk-text--text-01 pk-font-size--l pk-font-weight--bolder pk-line-height--md',
          isCompetition ? 'pk-d--block' : 'pk-d--none',
        )}
      >
        {t('interested_in')}
      </div>

      <div
        className={clsx('pk-flex--shrink-0 pk-d--flex pk-gap--s pk-gap-md--m pk-pb--l pk-pb-md--0', styles.quickLinks)}
      >
        {competitionOnlyItems.map((item) => (
          <a
            key={item.id}
            href={item.href}
            target={item.target}
            onClick={handleHeaderItemClick(item)}
            onAuxClick={handleHeaderItemClick(item)}
            className={clsx(
              'pk-text--text-01 pk-d--flex pk-justify-content--center pk-rounded--xl pk-p--m',
              styles.quickLinksBox,
            )}
          >
            {item.icon ? <Image src={`${imgBaseUrl}/imgml/${item.icon}`} alt={item.text} /> : item.text}
          </a>
        ))}

        {commonItems.map((m) => (
          <a
            key={m.id}
            aria-label={m.text}
            href={m.href}
            target={m.target}
            onClick={handleHeaderItemClick(m)}
            onAuxClick={handleHeaderItemClick(m)}
            className={clsx(
              'pk-flex--shrink-0 pk-d--flex pk-flex--column pk-gap--s pk-justify-content--between pk-rounded--xl pk-p--m',
              styles.quickLinksBox,
            )}
          >
            <div className='pk-d--flex pk-gap--s pk-justify-content--between'>
              {!!m.icon && <PkIcon name={m.icon} color='text-02' />}
              {!!m.customProperties?.livescores && !!liveMatches?.length && (
                <div className='pk-d--flex pk-align-items--center pk-bg--support-05 pk-text--text-05 pk-py--xs2 pk-px--s pk-font--base-semibold pk-font-size--xs pk-rounded--xl'>
                  {t('live')}
                </div>
              )}
            </div>
            <div
              className={clsx(
                'pk-text--text-01 pk-font-size--l pk-font--base-semibold pk-line-height--md pk-text--break',
                styles.quickLinksText,
              )}
            >
              {m.text}
            </div>
          </a>
        ))}
      </div>

      <div className={clsx('pk-py--l', isCompetition ? 'pk-d--block pk-d-md--none' : 'pk-d--none')}>&nbsp;</div>
    </div>
  );
};

export default memo(QuickLinks);
